import axios from 'axios'

// Enhanced Chat Service - Combines multiple chat APIs for optimal responses
import { API_BASE_URL } from '../config/api'

const ENHANCED_CHAT_API = API_BASE_URL // Main backend
const LOCAL_RAG_API = API_BASE_URL // Main backend

export interface BirthData {
  day: number
  month: number
  year: number
  hour: number
  minute: number
  latitude: number
  longitude: number
  place_of_birth?: string
}

export interface ChatMessage {
  id: string
  type: 'user' | 'assistant'
  content: string
  brief_answer?: string
  detailed_description?: string
  timestamp: Date
  confidence?: number
  sources?: string[]
  astrologicalContext?: any
}

export interface EnhancedChatResponse {
  success: boolean
  message: string
  brief_answer?: string
  detailed_description?: string
  confidence: number
  sources: string[]
  astrologicalContext?: {
    zodiacSign: string
    personalityTraits: string[]
    currentInfluences: string[]
    advice: string[]
  }
  error?: string
}

class EnhancedChatService {
  private birthData: BirthData | null = null
  private userId: string | null = null

  // Initialize chat with user data
  initialize(userId: string, birthData: BirthData) {
    this.userId = userId
    this.birthData = birthData
    console.log('🚀 Enhanced Chat Service initialized with:', { userId, birthData })
  }

  // Send message to enhanced chat system
  async sendMessage(message: string, conversationHistory: Array<{type: string, content: string, timestamp: Date}> = []): Promise<EnhancedChatResponse> {
    try {
      console.log('💬 Sending message to enhanced chat:', message)
      console.log('🌐 API URL:', `${ENHANCED_CHAT_API}/api/enhanced-chat`)
      console.log('📊 Request data:', {
        userMessage: message,
        birthData: this.birthData,
        userId: this.userId,
        conversationHistoryLength: conversationHistory.length
      })

      if (!this.birthData) {
        throw new Error('Birth data not initialized')
      }

      // Call the enhanced chat integration endpoint
      const response = await axios.post(`${ENHANCED_CHAT_API}/api/enhanced-chat`, {
        userMessage: message,
        userId: this.userId,
        birthData: {
          name: 'User',
          birthDate: `${this.birthData.year}-${String(this.birthData.month).padStart(2, '0')}-${String(this.birthData.day).padStart(2, '0')}`,
          birthTime: `${String(this.birthData.hour).padStart(2, '0')}:${String(this.birthData.minute).padStart(2, '0')}`,
          latitude: this.birthData.latitude,
          longitude: this.birthData.longitude,
          placeOfBirth: this.birthData.place_of_birth
        },
        conversationHistory: conversationHistory
      })

      console.log('✅ Enhanced chat response:', response.data)

      if (response.data.success) {
        return {
          success: true,
          message: response.data.aiResponse.response,
          brief_answer: response.data.aiResponse.brief_answer || response.data.aiResponse.response,
          detailed_description: response.data.aiResponse.detailed_description || response.data.aiResponse.response,
          confidence: response.data.aiResponse.confidence,
          sources: response.data.aiResponse.sources,
          astrologicalContext: response.data.enhancedContext?.aiReadyData
        }
      } else {
        console.error('❌ Enhanced chat returned error:', response.data)
        throw new Error(response.data.error || 'Failed to get response')
      }

    } catch (error: any) {
      console.error('❌ Enhanced chat error:', error)
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      })
      
      // Fallback to local RAG system
      return await this.fallbackToLocalRAG(message)
    }
  }

  // Fallback to local RAG system
  private async fallbackToLocalRAG(message: string): Promise<EnhancedChatResponse> {
    try {
      console.log('🔄 Falling back to local RAG system...')

      const response = await axios.post(`${LOCAL_RAG_API}/api/local-rag/chat`, {
        message,
        birthData: this.birthData,
        userId: this.userId
      })

      return {
        success: true,
        message: response.data.response || 'I can help you with astrological insights based on your profile.',
        confidence: 0.6,
        sources: ['Local Astrological Database'],
        astrologicalContext: {
          zodiacSign: this.getZodiacSign(this.birthData!.month, this.birthData!.day),
          personalityTraits: ['Based on your astrological profile'],
          currentInfluences: ['Using local astrological data'],
          advice: ['Focus on your natural strengths and trust your intuition']
        }
      }

    } catch (fallbackError) {
      console.error('❌ Local RAG fallback error:', fallbackError)
      
      return {
        success: false,
        message: 'I apologize, but I\'m having trouble accessing the astrological data right now. Please try again later.',
        confidence: 0.3,
        sources: [],
        error: 'Service temporarily unavailable'
      }
    }
  }

  // Get zodiac sign from birth date
  private getZodiacSign(month: number, day: number): string {
    const zodiacDates = [
      { sign: 'Capricorn', startMonth: 12, startDay: 22, endMonth: 1, endDay: 19 },
      { sign: 'Aquarius', startMonth: 1, startDay: 20, endMonth: 2, endDay: 18 },
      { sign: 'Pisces', startMonth: 2, startDay: 19, endMonth: 3, endDay: 20 },
      { sign: 'Aries', startMonth: 3, startDay: 21, endMonth: 4, endDay: 19 },
      { sign: 'Taurus', startMonth: 4, startDay: 20, endMonth: 5, endDay: 20 },
      { sign: 'Gemini', startMonth: 5, startDay: 21, endMonth: 6, endDay: 20 },
      { sign: 'Cancer', startMonth: 6, startDay: 21, endMonth: 7, endDay: 22 },
      { sign: 'Leo', startMonth: 7, startDay: 23, endMonth: 8, endDay: 22 },
      { sign: 'Virgo', startMonth: 8, startDay: 23, endMonth: 9, endDay: 22 },
      { sign: 'Libra', startMonth: 9, startDay: 23, endMonth: 10, endDay: 22 },
      { sign: 'Scorpio', startMonth: 10, startDay: 23, endMonth: 11, endDay: 21 },
      { sign: 'Sagittarius', startMonth: 11, startDay: 22, endMonth: 12, endDay: 21 }
    ]

    for (const zodiac of zodiacDates) {
      if (this.isDateInRange(month, day, zodiac)) {
        return zodiac.sign
      }
    }
    
    return 'Unknown'
  }

  // Check if date is in zodiac range
  private isDateInRange(month: number, day: number, zodiac: any): boolean {
    if (zodiac.startMonth === 12) {
      return (month === 12 && day >= zodiac.startDay) || (month === 1 && day <= zodiac.endDay)
    } else {
      return (month === zodiac.startMonth && day >= zodiac.startDay) || 
             (month === zodiac.endMonth && day <= zodiac.endDay)
    }
  }

  // Get Swiss Ephemeris data
  async getSwissEphemerisData(endpoint: string): Promise<any> {
    try {
      if (!this.birthData) {
        throw new Error('Birth data not initialized')
      }

      const response = await axios.post(`${ENHANCED_CHAT_API}/api/swiss-ephemeris/${endpoint}`, this.birthData)
      return response.data
    } catch (error) {
      console.error(`❌ Error getting Swiss Ephemeris ${endpoint}:`, error)
      return null
    }
  }

  // Get astrological insights
  async getAstrologicalInsights(query: string): Promise<any> {
    try {
      const response = await axios.post(`${LOCAL_RAG_API}/api/local-rag/insights`, {
        query,
        birthData: this.birthData
      })
      return response.data
    } catch (error) {
      console.error('❌ Error getting astrological insights:', error)
      return null
    }
  }

  // Get system status
  async getSystemStatus(): Promise<any> {
    try {
      const [enhancedStatus, localStatus] = await Promise.all([
        axios.get(`${ENHANCED_CHAT_API}/health`).catch(() => ({ data: { status: 'offline' } })),
        axios.get(`${LOCAL_RAG_API}/health`).catch(() => ({ data: { status: 'offline' } }))
      ])

      return {
        enhancedChat: enhancedStatus.data.status,
        localRAG: localStatus.data.status,
        timestamp: new Date().toISOString()
      }
    } catch (error) {
      console.error('❌ Error getting system status:', error)
      return { status: 'error' }
    }
  }

  // Get chat recommendations
  getChatRecommendations(): string[] {
    return [
      "What are my personality traits?",
      "What are my current astrological influences?",
      "How can I use my strengths today?",
      "What advice do you have for me?",
      "Tell me about my zodiac sign",
      "What should I focus on this week?",
      "How can I improve my relationships?",
      "What career opportunities are coming?",
      "How can I enhance my spiritual growth?",
      "What challenges should I prepare for?"
    ]
  }

  // Get user's zodiac sign
  getUserZodiacSign(): string {
    if (!this.birthData) return 'Unknown'
    return this.getZodiacSign(this.birthData.month, this.birthData.day)
  }

  // Get birth data
  getBirthData(): BirthData | null {
    return this.birthData
  }

  // Get user ID
  getUserId(): string | null {
    return this.userId
  }
}

// Create singleton instance
export const enhancedChatService = new EnhancedChatService()

// Export types for use in components 